import { DatasetOrigin, TaskType } from '@/shared/domain/taskTypes';
import { isAiGeneratedDatasetName } from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import {
    isRetrievalAgentDatasetName,
    retrievalTaskTypeLabel,
} from '@/features/datasets/utils/retrievalAgentDataset';
import {
    CONTENT_TYPE_ITEMS,
    TEXT_LENGTH_ITEMS,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

export const DatasetSourceKind = {
    aiGeneration: 'ai_generation',
    retrievalAgent: 'retrieval_agent',
};

const asList = (value) => {
    if (Array.isArray(value) && value.length) return value;
    if (typeof value === 'string' && value.trim()) return [value];
    return [];
};

/** True when Data Overview should offer a read-only generation/retrieval details modal. */
export function shouldShowDatasetSourceDetails(origin, sourceConfig) {
    if (sourceConfig?.source === DatasetSourceKind.aiGeneration) return true;
    if (sourceConfig?.source === DatasetSourceKind.retrievalAgent) return true;
    return (
        origin === DatasetOrigin.specific_ai_fully_generated
        || origin === DatasetOrigin.specific_ai_agent
    );
}

/** File card with the AI-generated pill (Data Overview + wizard upload area). */
export function shouldShowAiGeneratedFileCard({ origin, sourceConfig, datasetName, usecaseName } = {}) {
    return (
        shouldShowDatasetSourceDetails(origin, sourceConfig)
        || isAiGeneratedDatasetName(datasetName, usecaseName)
        || isRetrievalAgentDatasetName(datasetName, usecaseName)
    );
}

/**
 * True when the dataset came from the Retrieval Agent (vs Generate with AI).
 *
 * The `{task}_public{n}` name is only a fallback for when the dataset_info
 * doc has not loaded yet — a known origin / source_config always wins, so a
 * user-named `…_public` file self-corrects once the real source arrives.
 */
export function isRetrievalAgentSource({ origin, sourceConfig, datasetName, usecaseName } = {}) {
    if (
        sourceConfig?.source === DatasetSourceKind.retrievalAgent
        || origin === DatasetOrigin.specific_ai_agent
    ) {
        return true;
    }
    if (sourceConfig?.source || origin) return false;
    return isRetrievalAgentDatasetName(datasetName, usecaseName);
}

/** AI file-card pill label: Retrieval Agent datasets read "AI-retrieved". */
export function datasetSourcePillLabel({ origin, sourceConfig, datasetName, usecaseName } = {}) {
    return isRetrievalAgentSource({ origin, sourceConfig, datasetName, usecaseName })
        ? 'AI-retrieved'
        : 'AI-generated';
}

/** Creator + source_config fields copied from a datasets_info API doc onto UI objects. */
export function sourceFieldsFromDatasetInfoDoc(doc) {
    if (!doc) return {};
    return {
        sourceConfig: doc.source_config || undefined,
        createdByUser: doc.created_by_user || undefined,
        createdDatetime: doc.created_datetime || undefined,
    };
}

export function formatCreatedByUser(user) {
    if (!user || typeof user !== 'object') return '';
    return user.name || user.email || user.sub || '';
}

export function sourceDetailsVariant(config) {
    if (!config) return 'classification';
    if (config.source === DatasetSourceKind.retrievalAgent) return 'retrieval';
    const taskType = config.taskType;
    if (taskType === TaskType.NER) return 'ner';
    if (taskType === TaskType.summarization || taskType === TaskType.contentGeneration) {
        return 'summarization';
    }
    if (config.isMultilabel) return 'multiLabel';
    return 'classification';
}

export function toUiGenerationSettings(settings) {
    const raw = settings || {};
    const languages = asList(raw.languages);
    return {
        domain: raw.domain || null,
        languages: languages.length ? languages : ['English'],
        toneStyles: asList(raw.tone_styles || raw.toneStyles),
        textLength: raw.text_length || raw.textLength || 'short',
        contentType: raw.content_type || raw.contentType || 'text_only',
        customGuidelines: raw.custom_guidelines || raw.customGuidelines || '',
        exclude: raw.exclude || '',
        examplesPrompt: raw.examples_prompt || raw.examplesPrompt || '',
    };
}

export function toUiTargetConfig(raw) {
    const config = raw || {};
    const languages = asList(config.languages);
    const toneStyles = asList(config.tone_styles || config.toneStyles);
    const textLength = config.text_length || config.textLength || '';
    const exclude = config.exclude || '';
    const hasAdvanced = Boolean(
        config.text_length
        || config.textLength
        || config.languages
        || config.tone_styles
        || config.toneStyles
        || exclude
    );
    const exampleTexts = Array.isArray(config.examples)
        ? config.examples.map((text) => (text || '').trim()).filter(Boolean)
        : [];
    const examplesPrompt = config.examples_prompt || config.examplesPrompt || '';
    return {
        description: config.description || '',
        examples: exampleTexts.length ? exampleTexts : examplesPrompt ? [examplesPrompt] : [''],
        examplesPrompt,
        exclude,
        textLength: textLength || 'short',
        languages: languages.length ? languages : ['English'],
        toneStyles,
        advancedOpen: hasAdvanced,
    };
}

/** API snake_case `source_config` → UI-shaped details object. */
export function normalizeSourceConfig(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const settings = raw.generation_settings || raw.generationSettings || null;
    const targetConfigs = raw.target_configs || raw.targetConfigs || {};
    return {
        source: raw.source,
        taskType: raw.task_type || raw.taskType,
        isMultilabel: Boolean(raw.is_multilabel ?? raw.isMultilabel),
        goal: raw.goal || '',
        description: raw.description || '',
        examples: raw.examples || [],
        datasetSize: raw.dataset_size ?? raw.datasetSize ?? null,
        recordCounts: raw.record_counts || raw.recordCounts || {},
        targetConfigs: Object.fromEntries(
            Object.entries(targetConfigs).map(([name, config]) => [name, toUiTargetConfig(config)])
        ),
        settings: toUiGenerationSettings(settings),
        languages: asList(raw.languages),
        labelNames: raw.label_names || raw.labelNames || [],
        labelDescriptions: raw.label_descriptions || raw.labelDescriptions || {},
        datasetSizeLimit: raw.dataset_size_limit ?? raw.datasetSizeLimit ?? null,
        domain: raw.domain || null,
        agentModel: raw.agent_model || raw.agentModel || null,
        toneStyles: asList(raw.tone_styles || raw.toneStyles),
        textLength: raw.text_length || raw.textLength || 'short',
        contentType: raw.content_type || raw.contentType || 'text_only',
        customGuidelines: raw.custom_guidelines || raw.customGuidelines || '',
        datasetsToExclude: raw.datasets_to_exclude || raw.datasetsToExclude || [],
        // Public hub ids the Retrieval Agent's export actually used (the
        // per-record dataset_card values), stamped after a successful run.
        sourceDatasets: raw.source_datasets || raw.sourceDatasets || [],
        createdAt: raw.created_at || raw.createdAt || null,
    };
}

export function exampleText(example) {
    if (!example) return '';
    if (typeof example === 'string') return example;
    return example.text || '';
}

export function exampleLabelsText(example) {
    if (!example || typeof example !== 'object') return '';
    const labels = example.response?.labels;
    if (Array.isArray(labels)) return labels.join(', ');
    return example.label || '';
}

/** Reference summary / output on a summarization or content-generation example. */
export function exampleResponseText(example) {
    if (!example || typeof example !== 'object') return '';
    return example.response?.summary || '';
}

export function targetNamesFromConfig(config) {
    if (!config) return [];
    const fromCounts = Object.keys(config.recordCounts || {});
    if (fromCounts.length) return fromCounts;
    const fromTargets = Object.keys(config.targetConfigs || {});
    if (fromTargets.length) return fromTargets;
    if (config.labelNames?.length) return config.labelNames;
    return [];
}

export function agentModelLabel(agentModel) {
    if (!agentModel) return '';
    if (agentModel.label) return agentModel.label;
    const provider = agentModel.provider || '';
    const name = agentModel.name || '';
    return [provider, name].filter(Boolean).join(' / ');
}

export const EMPTY_SOURCE_META = '-';

/** Figma empty metadata cell. Arrays join with ", ". */
export function displayOrDash(value) {
    if (value == null) return EMPTY_SOURCE_META;
    if (Array.isArray(value)) {
        const text = value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
        return text || EMPTY_SOURCE_META;
    }
    const text = String(value).trim();
    return text || EMPTY_SOURCE_META;
}

function lookupOptionLabel(items, id) {
    if (!id) return '';
    return items.find((item) => item.id === id)?.label || '';
}

function firstNonEmpty(...values) {
    for (const value of values) {
        if (value == null) continue;
        if (Array.isArray(value) && value.length) return value;
        if (typeof value === 'number') return value;
        if (typeof value === 'string' && value.trim()) return value;
    }
    return null;
}

function exampleIncludeSuffix(example) {
    if (!example || typeof example !== 'object' || !Object.prototype.hasOwnProperty.call(example, 'include')) {
        return '';
    }
    return example.include === false ? ' Exclude' : ' Include';
}

function trimmedExampleStrings(examples) {
    return (examples || []).map((text) => (text || '').trim()).filter(Boolean);
}

function descriptionForTarget(config, name) {
    return (
        (config.labelDescriptions && config.labelDescriptions[name])
        || config.targetConfigs?.[name]?.description
        || ''
    ).trim();
}

/** Narrative rows for the details modal, labeled like the creation wizard's
 * Define Task step ("Goal" / "Description"). The Retrieval Agent wizard stores
 * its Description in `custom_guidelines`; older Generate-with-AI sessions kept
 * guidelines under `generation_settings`. Empty fields are omitted. */
export function sourceDetailsNarrativeRows(config) {
    if (!config) return [];
    const rows = [];
    if ((config.goal || '').trim()) {
        rows.push({ label: 'Goal', text: config.goal.trim() });
    }
    const description = (config.description || '').trim()
        || (config.customGuidelines || '').trim()
        || (config.settings?.customGuidelines || '').trim();
    if (description) {
        rows.push({ label: 'Description', text: description });
    }
    const isNer = config.taskType === TaskType.NER;
    const suffix = isNer ? 'entity description' : 'label description';
    targetNamesFromConfig(config).forEach((name) => {
        const text = descriptionForTarget(config, name);
        if (text) rows.push({ label: `${name} (${suffix})`, text });
    });
    return rows;
}

function exampleItem({ index, example, text, label, sideLabel }) {
    const body = (text || '').trim();
    const side = (label || '').trim();
    if (!body && !side) return null;
    return {
        key: `${index}-${body}-${side}`,
        title: `Example (${index})${exampleIncludeSuffix(example)}`,
        text: body,
        sideLabel: side && sideLabel ? sideLabel : null,
        sideValue: side && sideLabel ? side : '',
    };
}

/** Example blocks for the details modal. Empty examples are omitted. */
export function sourceDetailsExampleItems(config) {
    if (!config) return [];
    const variant = sourceDetailsVariant(config);
    const items = [];

    if (variant === 'classification') {
        Object.entries(config.targetConfigs || {}).forEach(([name, target]) => {
            trimmedExampleStrings(target?.examples).forEach((text) => {
                items.push({ text, label: name, sideLabel: 'Label', example: null });
            });
        });
        if (!items.length) {
            (config.examples || []).forEach((example) => {
                items.push({
                    example,
                    text: exampleText(example),
                    label: example?.label || example?.response?.label || '',
                    sideLabel: 'Label',
                });
            });
        }
        return items
            .map((item, index) => exampleItem({ index: index + 1, ...item }))
            .filter(Boolean);
    }

    const isContentGeneration = config.taskType === TaskType.contentGeneration;
    (config.examples || []).forEach((example) => {
        const text = exampleText(example);
        if (variant === 'summarization') {
            items.push({
                example,
                text,
                label: exampleResponseText(example),
                sideLabel: isContentGeneration ? 'Output' : 'Summary',
            });
            return;
        }
        if (variant === 'multiLabel') {
            items.push({
                example,
                text,
                label: exampleLabelsText(example),
                sideLabel: 'Label',
            });
            return;
        }
        if (variant === 'retrieval') {
            // Generative retrieval examples pair an input with a wanted output.
            if (example?.input_text) {
                items.push({ example, text: example.input_text, label: text, sideLabel: 'Output' });
                return;
            }
            const labelList = Array.isArray(example?.labels)
                ? example.labels.filter(Boolean)
                : [];
            if (labelList.length) {
                items.push({
                    example,
                    text,
                    label: labelList.join(', '),
                    sideLabel: config.taskType === TaskType.NER ? 'Entities' : 'Labels',
                });
                return;
            }
        }
        items.push({
            example,
            text,
            label: example?.label || example?.response?.label || '',
            sideLabel: variant === 'retrieval' ? 'Label' : null,
        });
    });

    return items
        .map((item, index) => exampleItem({ index: index + 1, ...item }))
        .filter(Boolean);
}

function recordLimitDisplay(value) {
    if (value == null || value === '') return EMPTY_SOURCE_META;
    return `${value} records`;
}

/** Figma metadata grid. Always returns both columns; empty values are "-".
 *
 * For Retrieval Agent datasets a "Datasets used in retrieval" row is appended
 * after "Datasets to exclude": the public hub ids whose records made it into
 * the final dataset (comma-joined). While the run is still in flight
 * (`retrievalInProgress`) the row carries `loading: true` so the modal renders
 * an "In progress" loading outline instead of a value. */
export function sourceDetailsMetadata(raw, config, { retrievalInProgress = false } = {}) {
    const settings = raw?.generation_settings || raw?.generationSettings || {};
    const languages = firstNonEmpty(raw?.languages, settings.languages);
    const domain = firstNonEmpty(raw?.domain, settings.domain);
    const textLengthId = firstNonEmpty(
        raw?.text_length,
        raw?.textLength,
        settings.text_length,
        settings.textLength
    );
    const contentTypeId = firstNonEmpty(
        raw?.content_type,
        raw?.contentType,
        settings.content_type,
        settings.contentType
    );
    const tone = firstNonEmpty(
        raw?.tone_styles,
        raw?.toneStyles,
        settings.tone_styles,
        settings.toneStyles
    );
    const excludeList = firstNonEmpty(raw?.datasets_to_exclude, raw?.datasetsToExclude);
    const excludeText = Array.isArray(excludeList) ? excludeList : firstNonEmpty(settings.exclude);
    const recordLimit = firstNonEmpty(
        raw?.dataset_size_limit,
        raw?.datasetSizeLimit,
        raw?.dataset_size,
        raw?.datasetSize
    );
    const model = firstNonEmpty(
        raw?.agent_model?.name,
        raw?.agentModel?.name,
        agentModelLabel(raw?.agent_model || raw?.agentModel || config?.agentModel)
    );

    const right = [
        { label: 'Languages', value: displayOrDash(languages) },
        {
            label: 'Task Type',
            value: displayOrDash(
                retrievalTaskTypeLabel({
                    task_type: config?.taskType || raw?.task_type || raw?.taskType,
                    is_multilabel: config?.isMultilabel ?? raw?.is_multilabel ?? raw?.isMultilabel,
                })
            ),
        },
        { label: 'Model (provider)', value: displayOrDash(model) },
        { label: 'Tone & Style', value: displayOrDash(tone) },
        { label: 'Content type', value: displayOrDash(lookupOptionLabel(CONTENT_TYPE_ITEMS, contentTypeId)) },
        { label: 'Datasets to exclude', value: displayOrDash(excludeText) },
    ];

    if (config?.source === DatasetSourceKind.retrievalAgent) {
        const usedList = firstNonEmpty(raw?.source_datasets, raw?.sourceDatasets, config?.sourceDatasets);
        right.push({
            label: 'Datasets used in retrieval',
            value: displayOrDash(usedList),
            wrap: true,
            loading: Boolean(retrievalInProgress),
        });
    }

    return {
        left: [
            { label: 'Active Labels', value: displayOrDash(targetNamesFromConfig(config)) },
            { label: 'Domain', value: displayOrDash(domain) },
            { label: 'Record limit', value: recordLimitDisplay(recordLimit) },
            { label: 'Text length', value: displayOrDash(lookupOptionLabel(TEXT_LENGTH_ITEMS, textLengthId)) },
        ],
        right,
    };
}

